import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { UserPlus, Copy, CheckCircle, Building2, Mail } from "lucide-react";
import { apiClient, UsageSummary } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import type { Organization, OrganizationUser } from "../../types";
import { Modal } from "../../components/common/ui/modal";
import Button from "../../components/common/ui/button/Button";
import { Table } from "../../components/common/tables/Table";
import { useOrg } from "../../contexts/OrgContext.tsx";
import { OrganizationRole } from "@secure-caseworker/shared";
import { useToast } from "../../contexts/ToastContext.tsx";
import { QuotaBar } from "../../components/common/QuotaBar";
import Loading from "../../components/common/ui/loading/index.tsx";

interface OrganizationDashboardProps {}

interface CombinedMemberRow {
  id: string;
  userId: string;
  name: string;
  email: string;
  role: OrganizationRole;
  createdAt: string;
  isPending: boolean;
  invitationId?: string;
}

export const OrganizationDashboard: React.FC<
  OrganizationDashboardProps
> = () => {
  const { setAlert } = useAlert();
  const { showToast } = useToast();

  const [members, setMembers] = useState<OrganizationUser[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSignupLinkModal, setShowSignupLinkModal] = useState(false);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [signupLink, setSignupLink] = useState("");
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const { orgId, navigateOrgUrl } = useOrg();

  // Invite form
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrganizationRole>(
    "staff" as OrganizationRole,
  );

  useEffect(() => {
    if (orgId) {
      const loadAll = async () => {
        await Promise.all([
          loadOrganization(),
          loadMembers(),
          loadInvitations(),
          loadUsageSummary(),
        ]);
        setLoading(false);
      };
      loadAll();
    }
    setLoading(false);
  }, [orgId]);

  const loadOrganization = async () => {
    if (!orgId) return;

    try {
      const org = await apiClient.getOrganization(orgId);
      setOrganization(org);
      return org;
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load organization",
      });
    }
  };

  const loadMembers = async () => {
    if (!orgId) return;

    try {
      const membersList = await apiClient.listOrganizationMembers(orgId);
      setMembers(membersList);
      return membersList;
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to load members",
      });
    }
  };

  const loadInvitations = async () => {
    if (!orgId) return;

    try {
      const invitesList = await apiClient.listInvitations(orgId);
      setInvitations(invitesList);
      return invitesList;
    } catch (error) {
      // Invitations might not be implemented yet, fail silently
      console.error("Failed to load invitations:", error);
    }
  };

  const loadUsageSummary = async () => {
    if (!orgId) return;

    try {
      const usage = await apiClient.getOrganizationUsage(orgId);
      setUsageSummary(usage);
      return usage;
    } catch (error) {
      console.error("Failed to load usage summary:", error);
    }
  };

  const handleSendInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orgId) return;

    setLoading(true);
    try {
      const invitation = await apiClient.createInvitation(
        orgId,
        inviteEmail,
        inviteRole,
      );

      // Generate signup link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/#/signup?token=${invitation.token}`;
      setSignupLink(link);

      setAlert({
        variant: "success",
        title: "Success",
        message: "Invitation created successfully",
      });
      setShowInviteModal(false);
      setShowSignupLinkModal(true);
      setInviteEmail("");
      setInviteRole("staff" as OrganizationRole);
      loadInvitations();
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Failed to send invitation",
        "error",
      );
      setShowInviteModal(false);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(signupLink);
    setAlert({
      variant: "success",
      title: "Copied",
      message: "Signup link copied to clipboard",
    });
  };

  // Blend members and invitations into a single table
  const combinedData = useMemo(() => {
    const memberRows: CombinedMemberRow[] = members.map((m) => ({
      id: m.id,
      userId: m.userId,
      name: m.user?.fullName || "N/A",
      email: m.user?.email || "",
      role: m.role,
      createdAt: new Date(m.invitedAt).toLocaleDateString(),
      isPending: false,
    }));

    const invitationRows: CombinedMemberRow[] = invitations.map((inv) => ({
      id: inv.id,
      userId: inv.id, // Use invitation ID as userId for navigation
      name: "Pending",
      email: inv.email,
      role: inv.role as OrganizationRole,
      createdAt: "Pending",
      isPending: true,
      invitationId: inv.id,
    }));

    return [...memberRows, ...invitationRows];
  }, [members, invitations]);

  const tableColumns = [
    {
      header: "Name",
      accessor: "name" as const,
      render: (_value: any, row: CombinedMemberRow) => (
        <div>
          <Link
            className="text-brand-600 hover:text-brand-700 dark:text-brand-400 hover:underline"
            to={navigateOrgUrl(`/members/${row.userId}`)}
          >
            {row.name}
            {row.isPending && (
              <span className="ml-2 inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                Pending
              </span>
            )}
          </Link>
        </div>
      ),
    },
    {
      header: "Email",
      accessor: "email" as const,
      render: (value: string) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
      ),
    },
    {
      header: "Role",
      accessor: "role" as const,
      render: (value: string) => (
        <div className="text-sm capitalize text-gray-900 dark:text-gray-100">
          {value}
        </div>
      ),
    },
    {
      header: "Created",
      accessor: "createdAt" as const,
      render: (value: string) => (
        <div className="text-sm text-gray-600 dark:text-gray-400">{value}</div>
      ),
    },
  ];

  if (loading) {
    return <Loading />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="mb-2 flex items-center gap-3 text-title-md2 font-semibold text-black dark:text-white">
            <Building2 size={32} />
            {organization?.name || "Organization Management"}
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage members and view usage for this organization
          </p>
        </div>
        <Button
          onClick={() => setShowInviteModal(true)}
          disabled={!orgId || organization?.isBeta}
          tooltip={() =>
            organization?.isBeta ? "Cannot invite users during beta" : null
          }
        >
          <UserPlus size={18} className="mr-2" />
          Invite Member
        </Button>
      </div>

      {/* Organization Selector */}

      {!organization ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-white/[0.03]">
          <Building2
            size={64}
            className="mx-auto mb-4 opacity-30 text-gray-400"
          />
          <p className="text-gray-600 dark:text-gray-400">
            No organizations found. Contact your superadmin.
          </p>
        </div>
      ) : (
        <>
          {usageSummary && (
            <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
              <h2 className="mb-4 text-title-sm font-semibold text-black dark:text-white">
                Usage & Quotas
              </h2>
              <div className="flex flex-wrap gap-6">
                <QuotaBar
                  label="Envelopes"
                  used={usageSummary.envelopes.used}
                  limit={usageSummary.envelopes.quota}
                  unit=""
                />
                <QuotaBar
                  label="Storage"
                  used={usageSummary.storage.usedMb}
                  limit={usageSummary.storage.limitMb}
                  unit="MB"
                  formatValue={(v) =>
                    v >= 1024
                      ? `${(v / 1024).toFixed(1)} GB`
                      : `${v.toFixed(0)} MB`
                  }
                />
                <QuotaBar
                  label="Users"
                  used={usageSummary.users.count}
                  limit={usageSummary.users.limit}
                />
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03] md:p-6">
            <h2 className="mb-6 flex items-center gap-2 text-title-sm font-semibold text-black dark:text-white">
              <Mail size={20} />
              Members & Invitations ({combinedData.length})
            </h2>

            {combinedData.length === 0 ? (
              <div className="py-12 text-center">
                <Mail
                  size={64}
                  className="mx-auto mb-4 opacity-30 text-gray-400"
                />
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  No members or invitations yet. Invite someone to get started.
                </p>
              </div>
            ) : (
              <Table data={combinedData} columns={tableColumns} />
            )}
          </div>
        </>
      )}

      {/* Invite Member Modal */}
      <Modal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        title="Invite Member"
        maxWidth="500px"
      >
        <form onSubmit={handleSendInvite} className="space-y-5">
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Email Address *
            </label>
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              placeholder="user@example.com"
              className="w-full rounded-sm border border-gray-300 bg-white px-4 py-2.5 text-black placeholder-gray-500 focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-boxdark dark:text-white dark:placeholder-gray-400"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Role *
            </label>
            <select
              value={inviteRole}
              onChange={(e) =>
                setInviteRole(e.target.value as OrganizationRole)
              }
              className="w-full rounded-sm border border-gray-300 bg-white px-4 py-2.5 text-black focus:border-brand-500 focus:outline-none dark:border-gray-700 dark:bg-boxdark dark:text-white"
            >
              <option value="admin">Admin - Full access to organization</option>
              <option value="staff">Staff - Can manage assigned cases</option>
              <option value="client">
                Client - Limited access to assigned cases
              </option>
            </select>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowInviteModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Creating..." : "Create Invitation"}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Signup Link Modal */}
      <Modal
        isOpen={showSignupLinkModal}
        onClose={() => setShowSignupLinkModal(false)}
        title="Invitation Created"
        maxWidth="500px"
      >
        <div>
          <div className="mb-6 flex items-center gap-3 rounded-sm border border-green-300 bg-green-50 p-4 dark:border-green-800 dark:bg-green-900/20">
            <CheckCircle
              size={20}
              className="text-green-600 dark:text-green-400"
            />
            <span className="text-sm font-medium text-green-700 dark:text-green-300">
              Invitation created successfully!
            </span>
          </div>

          <p className="mb-4 text-sm text-gray-700 dark:text-gray-300">
            Share this link with the invited member to allow them to sign up:
          </p>

          <div className="mb-6 break-all rounded-sm border border-gray-300 bg-gray-50 p-4 text-sm text-black dark:border-gray-700 dark:bg-boxdark dark:text-white">
            {signupLink}
          </div>

          <Button onClick={handleCopyLink} className="w-full">
            <Copy size={18} className="mr-2" />
            Copy Link to Clipboard
          </Button>
        </div>
      </Modal>
    </div>
  );
};
